import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, BellRing, LogOut, Users } from "lucide-react";
import { API } from "@/lib/api";

const THRESHOLDS = [
  { v: 1_000_000, label: "$1M+" },
  { v: 10_000_000, label: "$10M+" },
];

export const useUserCount = () => {
  const [n, setN] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/users/count`).then((r) => setN(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  return n;
};

export default function Header({ minUsd, onThreshold, prices, alertCount, email, onLogout }) {
  const users = useUserCount();
  return (
    <header className="sticky top-0 z-30 border-b border-[#1E2738] bg-[#080B10]/85 backdrop-blur-xl">
      <div className="flex flex-wrap items-center gap-4 px-4 py-3 lg:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-[#2E3C54] bg-[#0F141C]">
            <Activity className="h-5 w-5 text-[#00E5FF]" />
          </div>
          <div>
            <h1 data-testid="app-title" className="font-display text-xl font-bold tracking-tight">
              WHALE<span className="text-[#00E5FF]">/</span>FLOW
            </h1>
            <p className="overline !text-[10px]">exchange flow surveillance</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="live-sync-badge">
          <span className="live-dot" />
          <span className="font-mono text-xs text-slate-400">LIVE</span>
        </div>
        <div className="hidden flex-1 items-center gap-5 overflow-hidden xl:flex" data-testid="price-ticker">
          {["BTC", "ETH", "XRP", "SOL", "QNT"].map((c) => (
            <span key={c} className="font-mono text-xs text-slate-400">
              {c} <span className="text-slate-100">${prices?.[c]?.toLocaleString() ?? "—"}</span>
            </span>
          ))}
          <span className="font-mono text-xs text-slate-500" data-testid="header-price-targets">
            TARGETS <span className="text-[#00E5FF]">XRP $589</span> · <span className="text-[#FFB300]">BTC $175,000</span>
          </span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-md border border-[#1E2738] p-1" data-testid="header-threshold-filter">
            {THRESHOLDS.map((t) => (
              <button
                key={t.v}
                data-testid={`threshold-${t.v}`}
                onClick={() => onThreshold(t.v)}
                className={`chip !border-0 ${minUsd === t.v ? "active" : ""}`}
              >
                {t.label}
              </button>
            ))}
            {!THRESHOLDS.some((t) => t.v === minUsd) && (
              <span className="chip active !border-0" data-testid="threshold-custom">
                ${(minUsd / 1e6).toFixed(1)}M+
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 rounded-md border border-[#2E3C54] px-2.5 py-1.5" title="Registered users" data-testid="user-count-badge">
            <Users className="h-4 w-4 text-[#00E5FF]" />
            <span className="font-mono text-xs">{users?.total ?? "…"} users · {users?.visitors?.toLocaleString() ?? "…"} visitors</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-md border border-[#2E3C54] px-2.5 py-1.5" data-testid="alert-count-badge">
            <BellRing className="h-4 w-4 text-[#FFB300]" />
            <span className="font-mono text-xs">{alertCount}</span>
          </div>
          <button onClick={onLogout} data-testid="logout-button" title={email}
            className="flex items-center gap-1.5 rounded-md border border-[#1E2738] px-2.5 py-1.5 text-xs text-slate-400 transition-colors hover:text-white">
            <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </div>
    </header>
  );
}
